import { CalendarDays, Megaphone, Sparkles, User } from 'lucide-react';

import { NOTICE_TYPES, noticeTimeAgo } from '@/features/teacher/types/notices';
import { formatInstituteDate } from '@/features/teacher/utils';
import { cn } from '@/lib/utils';
import type { StudentNotice } from '@/types/student';

interface StudentNoticeCardProps {
  notice: StudentNotice;
  /**
   * `full` is the notices section's card — the whole notice, three to a row.
   * `compact` is the overview's line on it: the kind, the date and the title,
   * and the section is where the child goes to read the rest.
   */
  variant?: 'full' | 'compact';
}

/**
 * One notice, as a child reads it.
 *
 * Written once and used by both screens that show a notice, so the overview and
 * the notices section cannot drift apart in what they say or how a kind is
 * coloured (CLAUDE.md §3). The label and colour of each kind come from the
 * teacher feature's one map rather than a second copy here: an "Event" must not
 * be green on one screen and amber on the other.
 */
export function StudentNoticeCard({ notice, variant = 'full' }: StudentNoticeCardProps) {
  const kind = NOTICE_TYPES[notice.noticeType];

  const typeStyle = {
    GENERAL: {
      badgeBg: 'bg-emerald-500/10 dark:bg-emerald-500/15',
      badgeText: 'text-emerald-700 dark:text-emerald-300',
      badgeBorder: 'border-emerald-500/25 dark:border-emerald-500/30',
      dot: 'bg-emerald-500',
    },
    EVENT: {
      badgeBg: 'bg-indigo-500/10 dark:bg-indigo-500/15',
      badgeText: 'text-indigo-700 dark:text-indigo-300',
      badgeBorder: 'border-indigo-500/25 dark:border-indigo-500/30',
      dot: 'bg-indigo-500',
    },
    REMINDER: {
      badgeBg: 'bg-violet-500/10 dark:bg-violet-500/15',
      badgeText: 'text-violet-700 dark:text-violet-300',
      badgeBorder: 'border-violet-500/25 dark:border-violet-500/30',
      dot: 'bg-violet-500',
    },
    ACADEMIC: {
      badgeBg: 'bg-sky-500/10 dark:bg-sky-500/15',
      badgeText: 'text-sky-700 dark:text-sky-300',
      badgeBorder: 'border-sky-500/25 dark:border-sky-500/30',
      dot: 'bg-sky-500',
    },
    IMPORTANT: {
      badgeBg: 'bg-amber-500/10 dark:bg-amber-500/15',
      badgeText: 'text-amber-700 dark:text-amber-300',
      badgeBorder: 'border-amber-500/25 dark:border-amber-500/30',
      dot: 'bg-amber-500',
    },
  }[notice.noticeType] ?? {
    badgeBg: kind.bg,
    badgeText: kind.color,
    badgeBorder: kind.border,
    dot: kind.dot,
  };

  if (variant === 'compact') {
    return (
      <article
        className={cn(
          'group flex items-start gap-3 rounded-xl border p-3.5 transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5 bg-white dark:bg-[#1b1d34]',
          kind.border,
        )}
      >
        <span
          className={cn(
            'grid h-9 w-9 shrink-0 place-items-center rounded-xl border mt-0.5',
            typeStyle.badgeBg,
            typeStyle.badgeBorder,
            typeStyle.badgeText,
          )}
        >
          <Megaphone className="h-4 w-4" />
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center justify-between gap-2">
            <span
              className={cn(
                'w-fit rounded-full border px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider',
                typeStyle.badgeBg,
                typeStyle.badgeText,
                typeStyle.badgeBorder,
              )}
            >
              {kind.label}
            </span>
            <span
              className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-slate-500 dark:text-slate-400"
              title={`Put up ${noticeTimeAgo(notice.postedAt)}`}
            >
              <CalendarDays className="h-3.5 w-3.5" />
              {formatInstituteDate(notice.noticeDate)}
            </span>
          </div>
          <h4 className="line-clamp-2 text-sm font-bold leading-snug text-slate-800 dark:text-slate-100 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
            {notice.title}
          </h4>
          {notice.body && (
            <p className="line-clamp-2 text-[12px] font-medium text-slate-600 dark:text-slate-400 leading-relaxed whitespace-pre-line">
              {notice.body}
            </p>
          )}
        </div>
      </article>
    );
  }

  return (
    <article
      className={cn(
        'group flex flex-col justify-between gap-3.5 rounded-[22px] border border-slate-200/80 dark:border-white/10 bg-white dark:bg-[#1b1d34] p-5 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_28px_-6px_rgba(0,0,0,0.1)] dark:hover:shadow-[0_12px_28px_-6px_rgba(0,0,0,0.4)] hover:-translate-y-1 transition-all duration-200 relative overflow-hidden',
      )}
    >
      {/* Top accent glow indicator */}
      <div className={cn('absolute top-0 left-0 right-0 h-1', typeStyle.dot)} />

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 text-[11px] font-extrabold px-3 py-1 rounded-full border shadow-2xs uppercase tracking-wider',
              typeStyle.badgeBg,
              typeStyle.badgeText,
              typeStyle.badgeBorder,
            )}
          >
            <span className={cn('h-1.5 w-1.5 rounded-full', typeStyle.dot)} />
            {kind.label}
          </span>

          {notice.forThisClass && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 px-2.5 py-0.5 rounded-full">
              <Sparkles className="h-3 w-3" />
              For your class
            </span>
          )}
        </div>

        <h3 className="text-[15px] font-extrabold text-slate-800 dark:text-slate-100 leading-snug tracking-tight group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
          {notice.title}
        </h3>

        {notice.body && (
          <p className="text-xs text-slate-600 dark:text-slate-300 font-normal leading-relaxed whitespace-pre-line rounded-[14px] bg-slate-50/80 dark:bg-white/5 border border-slate-200/60 dark:border-white/10 p-3.5 mt-0.5">
            {notice.body}
          </p>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between gap-2 border-t border-slate-100 dark:border-white/5 pt-3.5 text-xs">
        <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300 font-semibold min-w-0">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[11px] font-bold">
            <User className="h-3.5 w-3.5" />
          </span>
          <span className="truncate text-xs">{notice.teacher}</span>
        </span>
        <span
          className="flex items-center gap-1.5 text-[11px] text-slate-400 dark:text-slate-400 font-medium shrink-0 bg-slate-50 dark:bg-white/5 px-2.5 py-1 rounded-md border border-slate-100 dark:border-white/5"
          title={`Put up ${noticeTimeAgo(notice.postedAt)}`}
        >
          <CalendarDays className="h-3.5 w-3.5 text-slate-400" />
          {formatInstituteDate(notice.noticeDate)}
        </span>
      </div>
    </article>
  );
}
